import { useEffect } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import clsx from 'clsx'

import {
  DATE_TIME_DATE_FMT_OPTS,
  DATE_TIME_TIME_FMT_OPTS,
  DATE_TIME_TY_OPTS,
  DateTimeDisplayOption,
} from '@makyoapp/makyo-common'
import * as Icon from '@makyoapp/makyo-icon/icon'
import { SelectDropdown, SingleIconSelect, Switch } from '@makyoapp/makyo-ui'

import { useCreateColumnContext } from '@/context/CreateColumnContext'
import { useLocaleColOrUserCb } from '@/hooks/useLocaleColOrUser'
import { TZ_AUTO } from '@/shared/constants'
import { LocaleDd } from '@/components/LocaleDd'
import { TzDd } from '@/components/TzDd'

import { Fld, Lbl } from './shared'

/**
 * handle display for date/time column
 */
function FieldDateTimeDisplay({
  allowedDisplayOptions = ['datetime', 'date', 'time'],
}: {
  allowedDisplayOptions?: DateTimeDisplayOption[]
}) {
  const { t } = useTranslation()
  const { id, value, setValue } = useCreateColumnContext()

  return (
    <Fld>
      <Lbl>{t('Display')}</Lbl>
      <SingleIconSelect
        id={`${id}-dt-type`}
        defaultSize={false}
        className='min-w-0'
        optionClassName={'w-8 h-8'}
        onClick={(opt) => {
          setValue({
            dtOptions: {
              ...value.dtOptions,
              dtDisplay: opt.id as DateTimeDisplayOption,
            },
          })
        }}
        options={[
          {
            id: 'datetime',
            icon: (selected) => {
              return (
                <Icon.Datetime16
                  className={clsx(
                    allowedDisplayOptions.includes('datetime') && 'group-hover:fill-white',
                    allowedDisplayOptions.includes('datetime') && selected ? 'fill-white' : 'fill-zinc-700',
                    !allowedDisplayOptions.includes('datetime') && 'opacity-30'
                  )}
                />
              )
            },
            title: 'Date & Time',
            disabled: !allowedDisplayOptions.includes('datetime'),
          },
          {
            id: 'date',
            icon: (selected) => {
              return (
                <Icon.Date16
                  className={clsx(
                    allowedDisplayOptions.includes('date') && 'group-hover:fill-zinc-700',
                    allowedDisplayOptions.includes('date') && selected ? 'fill-white' : 'fill-zinc-700',
                    !allowedDisplayOptions.includes('date') && 'opacity-30'
                  )}
                />
              )
            },
            title: 'Date',
            disabled: !allowedDisplayOptions.includes('date'),
          },
          {
            id: 'time',
            icon: (selected) => {
              return (
                <Icon.Clock16
                  className={clsx(
                    allowedDisplayOptions.includes('time') && 'group-hover:fill-white',
                    allowedDisplayOptions.includes('time') && selected ? 'fill-white' : 'fill-zinc-700',
                    !allowedDisplayOptions.includes('time') && 'opacity-30'
                  )}
                />
              )
            },
            title: 'Time',
            disabled: !allowedDisplayOptions.includes('time'),
          },
        ]}
        selected={
          value.dtOptions.dtDisplay && allowedDisplayOptions.includes(value.dtOptions.dtDisplay)
            ? value.dtOptions.dtDisplay
            : allowedDisplayOptions[0]
        }
      />
    </Fld>
  )
}

/**
 * handle date format options
 */
function FieldDateTimeDateFormat() {
  const { id, value, setValue } = useCreateColumnContext()
  const dtFmt =
    DATE_TIME_DATE_FMT_OPTS.find((df) => {
      return df.value === value.dtOptions.dtDateFormat
    }) || DATE_TIME_DATE_FMT_OPTS[2]

  const localeCb = useLocaleColOrUserCb()

  return (
    <Fld>
      <Lbl>
        <Trans>Date Format</Trans>
      </Lbl>
      <SelectDropdown
        menuContainerClassName='mk-ag-sdd'
        menuZIndex='z-305'
        portalMenu
        id={`${id}-dt-date-fmt`}
        value={dtFmt}
        menuHeadingClassName='text-light uppercase'
        onChange={(opt) => {
          setValue({
            dtOptions: {
              ...value.dtOptions,
              dtDateFormat: opt.value,
            },
          })
        }}
        renderOption={(opt) => {
          const now = new Date()
          const formatter = Intl.DateTimeFormat(localeCb(value.dtOptions), {
            dateStyle: opt.value as 'full',
            timeStyle: 'long',
            timeZone: value.dtOptions.dtTimezone === 'auto' ? undefined : value.dtOptions.dtTimezone,
          })
          return (
            <div>
              {opt.label} ({formatter.format(now)})
            </div>
          )
        }}
        optionLabel={(opt) => opt?.label}
        options={DATE_TIME_DATE_FMT_OPTS}
      />
    </Fld>
  )
}

/**
 * handle time format options
 */
function FieldDateTimeTimeFormat() {
  const { id, value, setValue } = useCreateColumnContext()
  const timeFmt =
    DATE_TIME_TIME_FMT_OPTS.find((tf) => {
      return tf.value === value.dtOptions.dtTimeFormat
    }) || DATE_TIME_TIME_FMT_OPTS[0]

  if (value.dtOptions.dtDateFormatType === 'user') {
    return null
  }

  return (
    <Fld>
      <Lbl>
        <Trans>Time Format</Trans>
      </Lbl>
      <SelectDropdown
        menuContainerClassName='mk-ag-sdd'
        menuZIndex='z-305'
        portalMenu
        id={`${id}-dt-time-fmt`}
        value={timeFmt}
        menuHeadingClassName='text-light uppercase'
        onChange={(opt) => {
          setValue({
            dtOptions: {
              ...value.dtOptions,
              dtTimeFormat: opt.value,
            },
          })
        }}
        renderOption={(opt) => opt.label}
        optionLabel={(opt) => opt.label}
        options={DATE_TIME_TIME_FMT_OPTS}
      />
    </Fld>
  )
}

function FieldDateTimezone() {
  const { id, value, setValue } = useCreateColumnContext()

  return (
    <>
      <Fld>
        <Lbl>
          <Trans>Time Zone</Trans>
        </Lbl>
        <TzDd
          menuContainerClassName='mk-ag-sdd'
          menuZIndex='z-305'
          portalMenu
          id={`${id}-dt-time-fmt`}
          withAuto
          value={value.dtOptions.dtTimezone || TZ_AUTO.value}
          onChange={(opt) => {
            setValue({
              dtOptions: {
                ...value.dtOptions,
                dtTimezone: opt,
              },
            })
          }}
        />
      </Fld>

      <Fld>
        <Lbl>
          <Trans>Display Timezone</Trans>
        </Lbl>
        <Switch
          id={`${id}-dt-tz-display`}
          checked={value.dtOptions.dtDisplayTz}
          onClick={(isChecked) => {
            setValue({
              dtOptions: {
                ...value.dtOptions,
                dtDisplayTz: isChecked,
              },
            })
          }}
        />
      </Fld>
    </>
  )
}

function FieldDateTimeDateFormatType() {
  const { id, setValue, value } = useCreateColumnContext()

  return (
    <>
      <Fld>
        <Lbl>
          <Trans>Date Format Type</Trans>
        </Lbl>
        <SelectDropdown
          onChange={(opt) => {
            setValue({
              dtOptions: {
                ...value.dtOptions,
                dtDateFormatType: opt.value,
              },
            })
          }}
          id={`${id}-dt-date-ty`}
          menuContainerClassName='mk-ag-sdd'
          menuZIndex='z-305'
          portalMenu
          value={DATE_TIME_TY_OPTS.find((o) => o.value === value.dtOptions.dtDateFormatType) || DATE_TIME_TY_OPTS[1]}
          options={DATE_TIME_TY_OPTS}
          renderOption={(opt) => {
            const Ic = Icon[opt.icon as keyof typeof Icon]
            return (
              <div className='flex-c'>
                {Ic && <Ic />}
                <Trans>{opt.label}</Trans>
              </div>
            )
          }}
          optionLabel={(opt) => opt.value}
        />
      </Fld>
      {value.dtOptions.dtDateFormatType === 'fixed' && (
        <Fld>
          <Lbl>
            <Trans>Locale</Trans>
          </Lbl>
          <LocaleDd
            menuContainerClassName='mk-ag-sdd'
            menuZIndex='z-305'
            portalMenu
            id={`${id}-dt-locale`}
            value={value.dtOptions.dtLocale}
            onChange={(opt) => {
              setValue({
                dtOptions: {
                  ...value.dtOptions,
                  dtLocale: opt,
                },
              })
            }}
          />
        </Fld>
      )}
    </>
  )
}

function FieldDateTimeShowSecond() {
  const { id, value, setValue } = useCreateColumnContext()

  return (
    <Fld>
      <Lbl>
        <Trans>Show Second</Trans>
      </Lbl>
      <Switch
        id={`${id}-dt-second`}
        checked={Boolean(value.dtOptions.dtDisplaySecond)}
        onClick={(isChecked) => {
          setValue({
            dtOptions: {
              ...value.dtOptions,
              dtDisplaySecond: isChecked,
            },
          })
        }}
      />
    </Fld>
  )
}

/**
 * handle create column date & time options field
 */
export default function FieldDateTime({
  allowedDisplayOptions = ['datetime', 'date', 'time'],
  autoSelectDisplayOptions = false,
  showTimezone = false,
}: {
  allowedDisplayOptions?: DateTimeDisplayOption[]
  autoSelectDisplayOptions?: boolean
  showTimezone?: boolean
}) {
  const { value, setValue } = useCreateColumnContext()
  const v =
    value.dtOptions.dtDisplay && allowedDisplayOptions.includes(value.dtOptions.dtDisplay)
      ? value.dtOptions.dtDisplay
      : allowedDisplayOptions[0]

  useEffect(() => {
    if (autoSelectDisplayOptions) {
      setValue((prev) => ({
        ...prev,
        dtOptions: {
          ...prev.dtOptions,
          dtDisplay: allowedDisplayOptions[0],
        },
      }))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <>
      <FieldDateTimeDisplay allowedDisplayOptions={allowedDisplayOptions} />
      <FieldDateTimeDateFormatType />
      {(allowedDisplayOptions.includes('date') || allowedDisplayOptions.includes('datetime')) &&
        ['datetime', 'date'].includes(v) && (
          <>
            <FieldDateTimeDateFormat />
          </>
        )}

      {(allowedDisplayOptions.includes('datetime') || allowedDisplayOptions.includes('time')) &&
        ['datetime', 'time'].includes(v) && (
          <>
            <FieldDateTimeTimeFormat />
            <FieldDateTimeShowSecond />
          </>
        )}
      {showTimezone && <FieldDateTimezone />}
    </>
  )
}
